
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, BarChart3, FileText, Eye, Check, X, Loader2, Clock } from "lucide-react";
import BtnSair from "../BtnSair";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCondominium } from "@/hooks/useCondominium";

const DashboardAdmin = () => {
  const { profile } = useAuth();
  const { data: condominium } = useCondominium();
  const navigate = useNavigate();

  // Buscar ocorrências aprovadas pelo síndico
  const { data: incidentsData, isLoading: incidentsLoading } = useQuery({
    queryKey: ["admin-incidents", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return { pending: 0, approved: 0, rejected: 0 };

      const { data: pendingIncidents } = await supabase
        .from("incidents")
        .select("id", { count: "exact" })
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "approved_by_sindico");

      const { data: approvedIncidents } = await supabase
        .from("incidents")
        .select("id", { count: "exact" })
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "approved_by_admin");

      const { data: rejectedIncidents } = await supabase
        .from("incidents")
        .select("id", { count: "exact" })
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "rejected_by_admin");

      return {
        pending: pendingIncidents?.length || 0,
        approved: approvedIncidents?.length || 0,
        rejected: rejectedIncidents?.length || 0
      };
    },
    enabled: !!profile?.condominium_id
  });

  // Buscar folhas de ponto aprovadas pelo síndico
  const { data: timesheetsData, isLoading: timesheetsLoading } = useQuery({
    queryKey: ["admin-timesheets", profile?.condominium_id],
    queryFn: async () => {
      if (!profile?.condominium_id) return { approved: 0, thisMonth: 0 };

      const { data: approvedTimesheets } = await supabase
        .from("timesheets")
        .select("id", { count: "exact" })
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "approved")
        .not("approved_by", "is", null);

      // Folhas aprovadas do mês atual
      const currentMonth = new Date();
      currentMonth.setDate(1);
      const { data: monthlyTimesheets } = await supabase
        .from("timesheets")
        .select("id", { count: "exact" })
        .eq("condominium_id", profile.condominium_id)
        .eq("status", "approved")
        .not("approved_by", "is", null)
        .gte("work_date", currentMonth.toISOString().split('T')[0]);

      return {
        approved: approvedTimesheets?.length || 0,
        thisMonth: monthlyTimesheets?.length || 0
      };
    },
    enabled: !!profile?.condominium_id
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {condominium?.name || "Condomínio"}
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            {profile?.full_name || "Administrador"} - Administrador
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <Clock className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ocorrências Pendentes</p>
                {incidentsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <p className="font-semibold">{incidentsData?.pending || 0} aguardando</p>
                )}
                <p className="text-xs text-gray-500">Aprovadas pelo síndico</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Check className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Ocorrências Aprovadas</p>
                {incidentsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <p className="font-semibold">{incidentsData?.approved || 0} processadas</p>
                )}
                <p className="text-xs text-gray-500">Pela administração</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <FileText className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Folhas de Ponto</p>
                {timesheetsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <p className="font-semibold">{timesheetsData?.approved || 0} aprovadas</p>
                )}
                <p className="text-xs text-gray-500">Pelo síndico</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardContent className="p-4">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Folhas do Mês</p>
                {timesheetsLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <p className="font-semibold">{timesheetsData?.thisMonth || 0} este mês</p>
                )}
                <p className="text-xs text-gray-500">Aprovadas pelo síndico</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Ações Rápidas</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button 
              className="w-full justify-start" 
              onClick={() => navigate("/ocorrencias")}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Ver ocorrências aprovadas
            </Button>
            <Button 
              className="w-full justify-start" 
              variant="outline"
              onClick={() => navigate("/folhas-ponto")}
            >
              <FileText className="h-4 w-4 mr-2" />
              Relatórios de folhas de ponto
            </Button>
          </div>
        </CardContent>
      </Card>

      <BtnSair />
    </div>
  );
};

export default DashboardAdmin;
